import React, { useState, useEffect, useCallback, useRef } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { Box, CssBaseline } from '@mui/material';
import AppBar from './components/AppBar/AppBar';
import MarketWatch from './components/MarketWatch/MarketWatch';
import MarketWatchExpandedView from './components/MarketWatch/ExpandedView/MarketWatchExpandedView';
import Orders from './components/Orders/Orders';
import Portfolio from './components/Portfolio';
import { fetchPositionsAction } from './store/actions/portfolio';
import { fetchPendingOrdersAction } from './store/actions/orders';
import Funds from './components/Funds/Funds';
import Charts from './components/Charts/Charts';
import usePositionUpdates from './hooks/wsUpdates/usePositonUpdates';
import DraggableDivider from './components/DraggableDivider';
import Login from './components/Login/Login';
import TwoFactorAuth from './components/TwoFactorAuth/TwoFactorAuth';
import Profile from './components/Profile/Profile';
import Referral from './components/Referral/Referral';
import { NotificationProvider } from './context/NotificationContext';
import { ContractProvider } from './context/ContractContext';
import { useDispatch } from 'react-redux';
import useOrderUpdates from './hooks/wsUpdates/useOrderUpdates';
import useSocketMessageUpdates from './hooks/wsUpdates/useSocketMessageUpdates';
import useAlertUpdates from './hooks/wsUpdates/useAlertUpdates';
import useBrokerageUpdates from './hooks/useBrokerageUpdates';
import useEquityMarginAlerts from './hooks/wsUpdates/useEquityMarginAlerts';
import octopusInstance from './services/octopus/octopusInstance';
import { wsManager } from './websocket/WebSocketManager';
import { fetchUserProfile, fetchUserSettings, fetchBrokerSettings, fetchMasterSettings, fetchExchangeSessionInfo } from './services/backendAPI';
import { fetchMarketWatchesAction, } from './store/actions/watchlist';
import { fetchMarginConfigAction } from './store/actions/margin';
import { fetchBrokerageAction } from './store/actions/brokerage';
import useMediaQuery from '@mui/material/useMediaQuery';
import BottomNav from './components/Navigation/BottomNav';
import Overview from './components/Overview/Overview';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { setProfileDataAction, setUserSettingsAction, setBrokerSettingsAction, setMasterSettingsAction, setExchangeSessionsAction } from './store/actions/profile';
import OrderWindowHOC from './components/OrderWindow/OrderWindowHOC';
import GTTModalHOC from './components/OrderWindow/GTTModalHOC';
import TopTicker, { TOP_TICKER_HEIGHT, TOP_TICKER_HEIGHT_MOBILE } from './components/TopTicker/TopTicker';
import PriceAlertModalHOC from './components/PriceAlerts/PriceAlertModalHOC';
import PayIn from './components/PayIn/PayIn';
import TradeHistory from './components/TradeHistory/TradeHistory';
import TradeHistoryDetails from './components/TradeHistory/TradeHistoryDetails';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import useFundsQuery from './hooks/useFundsQuery';
import { fetchFundsView } from './store/actions/funds';
import FundsProvider from './components/Common/FundsProvider';
import useFundsUpdates from './hooks/wsUpdates/useFundsUpdates';
import PnlHistoryDetails from './components/TradeHistory/PnlHistoryDetails';
import Ledger from './components/TradeHistory/Ledger';
import LedgerDetails from './components/TradeHistory/LedgerDetails';
import { refreshFundsAction } from './store/actions/funds';
import ContactUs from './components/ContactUs';
import CFDRiskDisclosureModal from './components/Common/CFDRiskDisclosureModal';
import Screeners from './components/Screeners/Screeners';
import PriceAlerts from './components/PriceAlerts/PriceAlerts';
import Notifications from './components/Notifications/Notifications';
import DashboardNotificationSnackbar from './components/Profile/DashboardNotificationSnackbar';

import brokerConfig from './config/brokerConfig';
import { useVisitorData } from '@fingerprintjs/fingerprintjs-pro-react';
import { useSelector } from 'react-redux';
import ZohoChatbot from './components/Chatbot/ZohoChatbot';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import Dashboard from './components/Dashboard';

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  // Offset for the fixed AppBar (64px) + the Top Ticker bar below it.
  height: `calc(100dvh - ${64 + TOP_TICKER_HEIGHT}px)`,
  marginTop: `${64 + TOP_TICKER_HEIGHT}px`,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    // AppBar (56) + ticker + BottomNav (56)
    height: `calc(100dvh - ${112 + TOP_TICKER_HEIGHT_MOBILE}px)`,
    marginTop: `${56 + TOP_TICKER_HEIGHT_MOBILE}px`,
    marginBottom: '56px',
    overflow: 'hidden',
  },
}));

// GTM injection logic
const injectGTM = (gtmId) => {
  if (!gtmId || window.gtmInjected) return;

  const script = document.createElement('script');
  script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');`;
  document.head.appendChild(script);

  const noscript = document.createElement('noscript');
  noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
  document.body.insertBefore(noscript, document.body.firstChild);

  window.gtmInjected = true;
};

const ContentPane = styled(Box)(({ width, theme }) => ({
  width: `${width}%`,
  overflow: 'auto',
  transition: 'width 0.1s ease',
  height: '100%',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    height: 'calc(100dvh - 56px)',
    paddingTop: '8px',
    paddingBottom: 0,
  },
}));

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const userData = useSelector(state => state.profile.userData);

  const { data: visitorData } = useVisitorData();

  useEffect(() => {
    if (visitorData && visitorData.visitorId) {
      localStorage.setItem('x-fingerprint-id', visitorData.visitorId);
    }
  }, [visitorData]);

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('isAuthenticated') === 'true';
  });

  useEffect(() => {
    if (brokerConfig.title) {
      document.title = brokerConfig.title;
    }
    if (brokerConfig.favicon) {
      const link = document.getElementById('dynamic-favicon') || document.querySelector("link[rel*='icon']");
      if (link) {
        link.href = brokerConfig.favicon;
      } else {
        const newLink = document.createElement('link');
        newLink.id = 'dynamic-favicon';
        newLink.rel = 'icon';
        newLink.href = brokerConfig.favicon;
        document.head.appendChild(newLink);
      }

      const appleLink = document.getElementById('dynamic-apple-favicon') || document.querySelector("link[rel*='apple-touch-icon']");
      if (appleLink) {
        appleLink.href = brokerConfig.favicon;
      }
    }
  }, []);

  useEffect(() => {
    if (brokerConfig.gtmId) {
      injectGTM(brokerConfig.gtmId);
    }
  }, []);

  const [is2FAVerified, setIs2FAVerified] = useState(() => {
    return localStorage.getItem('2faVerified') === 'true' && !!localStorage.getItem('auth_token');
  });
  // const [isValidToken, setIsValidToken] = useState(false);
  const [loading, setLoading] = useState(true);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const [activeComponent, setActiveComponent] = useState('profile');
  const [previousComponent, setPreviousComponent] = useState(null);

  // CFD Risk Disclosure Modal state
  const [showCFDModal, setShowCFDModal] = useState(false);
  // Zoho Support Chat state
  const [showSupportChat, setShowSupportChat] = useState(() => {
    if (sessionStorage.getItem('zoho_autoopen_support') === '1') {
      sessionStorage.removeItem('zoho_autoopen_support');
      return true;
    }
    return false;
  });
  const [canLoadSupportBot, setCanLoadSupportBot] = useState(true);
  const zohoChatbotRef = useRef(null);
  const loginBotRef = useRef(null);
  const isSupportPage = location.pathname.startsWith('/support');

  const cleanupZohoBots = useCallback(async () => {
    await Promise.resolve(loginBotRef.current?.cleanup?.());
    await Promise.resolve(zohoChatbotRef.current?.cleanup?.());
  }, []);

  const handleSupportChatTrigger = useCallback(async () => {
    await cleanupZohoBots();
    sessionStorage.setItem('zoho_autoopen_support', '1');
    window.location.reload();
  }, [cleanupZohoBots]);

  const enhancedSetActiveComponent = useCallback(async (newComponent, state = null) => {
    if (newComponent === 'support') {
      await cleanupZohoBots();
      sessionStorage.removeItem('zoho_autoopen_support');

      if (window.location.pathname === '/support') {
        window.location.reload();
      } else {
        window.location.assign('/support');
      }
      return;
    }

    navigate(`/${newComponent}`, { state });
    setPreviousComponent(activeComponent);
    setActiveComponent(newComponent);
  }, [activeComponent, cleanupZohoBots, navigate]);

  const [leftPaneWidth, setLeftPaneWidth] = useState(() => {
    const storedWidth = localStorage.getItem('leftPaneWidth');
    return storedWidth ? parseFloat(storedWidth) : 75;
  });

  const [chartSymbol, setChartSymbol] = useState(null);
  const [chartKey, setChartKey] = useState(0);

  const dispatch = useDispatch();

  let token = window.localStorage.getItem("auth_token");

  useEffect(() => {

    const initializeConnection = async () => {
      if (token && isAuthenticated && is2FAVerified) {
        try {
          const { data } = await fetchUserProfile();

          // await window.eventsCapture.onUserLogin.push({
          //   client_id: data.client_id,
          //   name: data.name,
          //   email: data.email_id,
          //   phone: data.phone_number,
          // });

          const { data: settings } = await fetchUserSettings();
          data.shortName = data.name.match(/\b(\w)/g).join("");
          localStorage.setItem("user_type", data.user_type);
          dispatch(setProfileDataAction(data));

          if (settings) {
            const processedSettings = {
              ...settings,
              client_id: data.client_id || settings.client_id
            };
            dispatch(setUserSettingsAction(processedSettings));
            dispatch(setUserSettingsAction(processedSettings));
          }

          try {
            const { data: brokerSettings } = await fetchBrokerSettings();
            if (brokerSettings) {
              dispatch(setBrokerSettingsAction(brokerSettings));
            }
          } catch (error) {
            console.error('Failed to fetch broker settings:', error);
          }

          try {
            const { data: masterSettings } = await fetchMasterSettings();
            if (masterSettings) {
              dispatch(setMasterSettingsAction(masterSettings));
            }
          } catch (error) {
            console.error('Failed to fetch master settings:', error);
          }

          // Not awaited: only the Profile market-status card needs it, so it must not delay startup
          fetchExchangeSessionInfo()
            .then(({ result }) => {
              if (Array.isArray(result)) {
                dispatch(setExchangeSessionsAction(result));
              }
            })
            .catch((error) => {
              console.error('Failed to fetch exchange session info:', error);
            });

          // Check if this is first login and show CFD risk disclosure
          const hasSeenCFDDisclosure = localStorage.getItem('cfd_disclosure_accepted');
          if (!hasSeenCFDDisclosure) {
            setShowCFDModal(true);
          }

          // Connect to WebSocket if tab is visible
          if (document.visibilityState === 'visible') {
            try {
              await octopusInstance.connect();
              console.log('WebSocket connected successfully');
            } catch (err) {
              console.error('Initial WebSocket connection error:', err);
            }
          }

          dispatch(fetchPositionsAction("historical"));
          dispatch(fetchPendingOrdersAction());
          dispatch(fetchMarketWatchesAction(true));

          // Fetch margin configuration with error handling (only once per session)
          const marginState = window.store?.getState()?.margin;
          const shouldFetchMarginConfig = !marginState?.marginConfig ||
            Object.keys(marginState.marginConfig).length === 0;

          if (shouldFetchMarginConfig && !marginState?.loading) {
            console.log('App startup: Fetching margin configuration...');
            dispatch(fetchMarginConfigAction())
              .then((result) => {
                if (result.status === 'success') {
                  console.log('App startup: Margin configuration loaded successfully');
                } else {
                  console.warn('App startup: Failed to load margin configuration:', result.message);
                }
              })
              .catch((error) => {
                console.error('App startup: Error loading margin configuration:', error);
              });
          } else {
            console.log('App startup: Margin configuration already available');
          }
          dispatch(fetchBrokerageAction())
            .then((result) => {
              if (result.status === 'success') {
                console.log('App startup: Brokerage data loaded successfully');
              } else {
                console.warn('App startup: Failed to load brokerage data:', result.message);
              }
            })
            .catch((error) => {
              console.error('App startup: Error loading brokerage data:', error);
            });
        } catch (error) {
          console.error('Connection error:', error);
          localStorage.clear();
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    initializeConnection();

    return () => {
      localStorage.removeItem("user_type");
    };
  }, [token, isAuthenticated, is2FAVerified, dispatch]);


  const connect = async () => {
    try {
      // Use octopusInstance
      await octopusInstance.connect();
      return true;
    } catch (error) {
      console.error('WebSocket connection error:', error);
      throw error;
    }
  }

  useEffect(() => {
    if (isAuthenticated && is2FAVerified) {
      // Connect to WebSocket with error handling
      const setupConnection = async () => {
        try {
          await connect();
          console.log('WebSocket connection established successfully');

          // Fetch data after connection is established
          dispatch(fetchMarketWatchesAction(true));
          dispatch(fetchFundsView());
        } catch (error) {
          console.error('Failed to connect to WebSocket:', error);
        }
      };

      setupConnection();
    }
  }, [isAuthenticated, is2FAVerified]);

  useOrderUpdates();
  useFundsUpdates();
  usePositionUpdates();
  useSocketMessageUpdates();
  useBrokerageUpdates();
  useEquityMarginAlerts();
  useAlertUpdates();

  useEffect(() => {
    localStorage.setItem('leftPaneWidth', leftPaneWidth.toString());
    // localStorage.setItem('token', auth_token);
  }, [leftPaneWidth]);

  // Check for first_login and redirect to funds page if true
  useEffect(() => {
    if (isAuthenticated) {
      const firstLogin = localStorage.getItem("first_login");
      console.log("first_login value:", firstLogin, "type:", typeof firstLogin);

      if (firstLogin === "true") {
        console.log('First login detected, redirecting to funds page');
        localStorage.setItem("first_login", "false");
        navigate('/funds');
      }
    }
  }, [isAuthenticated, is2FAVerified, navigate]);

  const handleDrag = (dragDiff) => {
    const containerWidth = document.querySelector('.MuiBox-root').offsetWidth;
    const newWidth = leftPaneWidth - (dragDiff / containerWidth * 100);
    if (newWidth >= 65 && newWidth <= 85) {
      setLeftPaneWidth(newWidth);
    }
  };

  const handleChartOpen = (symbol) => {
    setChartKey(prevKey => prevKey + 1);
    setChartSymbol(symbol);
    setActiveComponent('charts');
  };

  const handleLogin = async () => {
    setCanLoadSupportBot(false);
    await Promise.resolve(loginBotRef.current?.cleanup?.());
    setCanLoadSupportBot(true);
    setIsAuthenticated(true);
  };

  const handle2FAVerify = () => {
    setIs2FAVerified(true);
  };

  const handleLogout = async () => {
    await cleanupZohoBots();
    setCanLoadSupportBot(true);

    // Explicitly disconnect the WebSocket before clearing auth data
    octopusInstance.closeConnection();
    if (typeof wsManager !== 'undefined' && wsManager.disconnect) {
      wsManager.disconnect();
    }

    // Clear all auth and session-related storage
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('2faVerified');
    localStorage.removeItem('login_id');
    localStorage.removeItem('auth_token'); // Ensure the token is also cleared
    localStorage.removeItem('login_name');
    localStorage.removeItem('dashboardNotificationClosed');
    localStorage.removeItem('dashboardNotificationIndex');
    localStorage.removeItem('twofa_token');
    localStorage.removeItem('twofa_questions');
    localStorage.removeItem('twofa_type');
    localStorage.removeItem('2fa_lockout_expiry');
    localStorage.removeItem('2fa_setup_required');
    
    // Update local state to trigger navigation to login
    setIsAuthenticated(false);
    setIs2FAVerified(false);
    setShowSupportChat(false);
    sessionStorage.removeItem('zoho_autoopen_support');
    window.location.replace('/login');
  };

  const handleCFDModalAccept = () => {
    localStorage.setItem('cfd_disclosure_accepted', 'true');
    setShowCFDModal(false);
  };

  const handleCFDModalClose = () => {
    setShowCFDModal(false);
  };

  // Component to handle invalid /overview route
  const InvalidOverviewHandler = () => {
    useEffect(() => {
      console.log('⚠️ Invalid /overview route accessed - logging out user');
      handleLogout();
      // Redirect to login after logout (login will then redirect to appropriate default)
      navigate('/login');
    }, []);

    return null; // Return nothing while logout happens
  };

  const renderComponent = useCallback(() => {
    switch (activeComponent) {
      case 'dashboard':
        return <Dashboard />;
      case 'marketwatch':
        return <MarketWatch onChartOpen={handleChartOpen} />;
      case 'marketwatch-expanded':
        return <MarketWatchExpandedView />;
      case 'orders':
        return <Orders />;
      case 'portfolio':
        return <Portfolio />;
      case 'funds':
        return <Funds />;
      case 'charts': {
        const queryParams = new URLSearchParams(location.search);
        const exchange = queryParams.get('exchange') || chartSymbol?.exchange;
        const token = queryParams.get('token') || chartSymbol?.token;
        const pricePrecision = queryParams.get('precision') || queryParams.get('price_precision') || chartSymbol?.price_precision;

        if (exchange && token) {
          return (
            <Charts
              key={`${exchange}-${token}-${chartKey}`}
              exchange={exchange}
              token={token}
              pricePrecision={Number(pricePrecision) || 2}
              theme={theme}
            />
          );
        }
        return chartSymbol ? (
          <Charts
            key={chartKey}
            exchange={chartSymbol.exchange}
            token={chartSymbol.token}
            pricePrecision={chartSymbol.price_precision}
            theme={theme}
          />
        ) : null;
      }
      case 'profile':
        return <Profile />;
      case 'overview':
        return <Overview />;
      case 'tradehistory':
        return <TradeHistory />;
      case 'screeners':
        return <Screeners />;
      case 'price-alerts':
        return <PriceAlerts />;
      case 'notifications':
        return <Notifications />;
      case 'referral':
        return <Referral />;
      case 'support':
        return <ContactUs onChatTrigger={handleSupportChatTrigger} />;
      default:
        return <Profile />;
    }
  }, [activeComponent, chartSymbol, chartKey, handleSupportChatTrigger]);

  useEffect(() => {
    // Map of routes to components for activeComponent state
    const routeToComponent = {
      '/overview/tdv': 'overview',
      '/marketwatch-expanded': 'marketwatch-expanded',
      '/marketwatch': 'marketwatch',
      '/orders': 'orders',
      '/portfolio': 'portfolio',
      '/funds': 'funds',
      '/profile': 'profile',
      '/dashboard': 'dashboard',
      '/tradehistory': 'tradehistory',
      '/screeners': 'screeners',
      '/price-alerts': 'price-alerts',
      '/notifications': 'notifications',
      '/referral': 'referral',
      '/support': 'support',
      '/charts': 'charts'
    };

    // Find matching route and set active component
    const matchedComponent = Object.entries(routeToComponent)
      .find(([route]) => location.pathname.startsWith(route));

    if (matchedComponent) {
      setActiveComponent(matchedComponent[1]);
    } else if (location.pathname.startsWith('/overview')) {
      setActiveComponent('overview');
    } else {
      // For any other routes, default based on device type
      setActiveComponent('dashboard');
    }
  }, [location.pathname, isMobile]);

  // Close Zoho chat widget on any route change
  useEffect(() => {
    // Close/hide Zoho SalesIQ chat widget when navigating
    try {
      if (window.$zoho && window.$zoho.salesiq) {
        // Try to close the chat window
        if (window.$zoho.salesiq.floatwindow && typeof window.$zoho.salesiq.floatwindow.visible === 'function') {
          window.$zoho.salesiq.floatwindow.visible('hide');
        }
        // Try alternative method to close chat
        if (window.$zoho.salesiq.chat && typeof window.$zoho.salesiq.chat.close === 'function') {
          window.$zoho.salesiq.chat.close();
        }
      }
    } catch (error) {
      console.error('Error closing Zoho chat widget:', error);
    }
  }, [location.pathname]);

  // Add a function to monitor the connection status
  useEffect(() => {
    if (isAuthenticated && is2FAVerified) {
      let reconnectTimeout = null;
      let lastReconnectAttempt = 0;
      const RECONNECT_COOLDOWN = 5000; // 5 seconds cooldown between reconnect attempts

      // Only attempt reconnection if not already trying and cooldown period has passed
      const attemptReconnectIfNeeded = () => {
        const now = Date.now();
        if (now - lastReconnectAttempt < RECONNECT_COOLDOWN) {
          console.log('Reconnection attempt too soon, skipping');
          return;
        }

        // Only reconnect if not connected
        if (!octopusInstance.isConnected()) {
          console.log('WebSocket disconnected, attempting to reconnect...');
          lastReconnectAttempt = now;

          octopusInstance.connect()
            .then(() => {
              console.log('Reconnection successful, refreshing market and funds data');
              // After successful reconnection, reload watchlists and funds to refresh data
              dispatch(fetchMarketWatchesAction(true));
              dispatch(refreshFundsAction(true));
            })
            .catch(error => {
              console.error('Reconnection failed:', error);
            });
        } else {
          // Connection is active, but don't refresh data on every check
          // Only log that connection is active
          console.log('Connection active - maintaining WebSocket connection');
        }
      };

      // Handle visibility change
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          console.log('Tab became visible, scheduling connection check');

          // Clear any existing timeout
          if (reconnectTimeout) {
            clearTimeout(reconnectTimeout);
          }

          // Schedule a single reconnect attempt after a short delay
          // This prevents multiple rapid attempts if visibility changes quickly
          reconnectTimeout = setTimeout(() => {
            attemptReconnectIfNeeded();
            reconnectTimeout = null;
          }, 1000);
        }
      };

      // Set up periodic connection check - less frequent
      const connectionCheckInterval = setInterval(attemptReconnectIfNeeded, 30000);

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        if (reconnectTimeout) {
          clearTimeout(reconnectTimeout);
        }
        clearInterval(connectionCheckInterval);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [isAuthenticated, is2FAVerified, dispatch]);

  let mainContent;

  if (!isAuthenticated) {
    const loginRoutes = (
      <NotificationProvider>
        <ContractProvider>
          <FundsProvider>
            <Routes>
              <Route path="/login" element={<Login onLogin={handleLogin} onVerify={handle2FAVerify} />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </FundsProvider>
        </ContractProvider>
      </NotificationProvider>
    );

    mainContent = loginRoutes;
  } else if (!is2FAVerified) {
    mainContent = (
      <NotificationProvider>
        <ContractProvider>
          <FundsProvider>
            <Routes>
              <Route path="*" element={<TwoFactorAuth onVerify={handle2FAVerify} />} />
            </Routes>
          </FundsProvider>
        </ContractProvider>
      </NotificationProvider>
    );
  } else {
    mainContent = (
      <NotificationProvider>
        <ContractProvider>
          <FundsProvider>
            <AppBar
              onLogout={handleLogout}
              activeComponent={activeComponent}
              onNavigate={enhancedSetActiveComponent}
              isMobile={isMobile}
            />
            <TopTicker />
            {isMobile ? (
              <BottomNav
                activeComponent={activeComponent}
                onNavigate={enhancedSetActiveComponent}
              />
            ) : null}
            <Container>
              {!isMobile && activeComponent !== 'marketwatch' && activeComponent !== 'marketwatch-expanded' && (
                <>
                  <ContentPane width={100 - leftPaneWidth}>
                    <MarketWatch onChartOpen={handleChartOpen} />
                  </ContentPane>
                  <DraggableDivider onDrag={handleDrag} />
                </>
              )}
              <ContentPane
                width={isMobile || activeComponent === 'marketwatch' || activeComponent === 'marketwatch-expanded' ? 100 : leftPaneWidth}
                sx={{
                  overflow: "hidden",
                  p: 2,
                  [`${theme.breakpoints.down('md')}`]: {
                    pt: 1,
                    px: 1,
                    pb: 0,
                  }
                }}
              >
                <Box sx={{
                  height: '100%',
                  overflow: 'hidden',
                }}>
                  <Routes>
                    <Route path="/overview/tdv/:exchange/:token/:price_precision" element={<Overview />} />
                    <Route path="/overview/tdv/:exchange/:token" element={<Overview />} />
                    <Route
                      path="/overview"
                      element={<InvalidOverviewHandler />}
                    />
                    <Route
                      path="/"
                      element={
                        <Navigate
                          to={"/dashboard"}
                          replace
                        />
                      }
                    />
                    <Route path="/marketwatch" element={renderComponent()} />
                    <Route path="/marketwatch-expanded" element={renderComponent()} />
                    <Route path="/orders" element={renderComponent()} />
                    <Route path="/portfolio" element={renderComponent()} />
                    <Route path="/funds" element={renderComponent()} />
                    <Route path="/profile" element={renderComponent()} />
                    <Route path="/charts" element={renderComponent()} />
                    <Route path="/dashboard" element={renderComponent()} />
                    <Route path="/tradehistory" element={renderComponent()} />
                    <Route path="/screeners" element={renderComponent()} />
                    <Route path="/price-alerts" element={renderComponent()} />
                    <Route path="/notifications" element={renderComponent()} />
                    <Route path="/referral" element={renderComponent()} />
                    <Route path="/support" element={<ContactUs onChatTrigger={handleSupportChatTrigger} />} />
                    <Route path="/trade-history" element={<TradeHistory />} />
                    <Route path="/trade-history/details" element={<TradeHistoryDetails />} />
                    <Route path="/pnl-history/details" element={<PnlHistoryDetails />} />
                    <Route path="/ledger" element={<Ledger />} />
                    <Route path="/ledger/details" element={<LedgerDetails />} />
                    <Route
                      path="*"
                      element={
                        <Navigate
                          to={"/dashboard"}
                          replace
                        />
                      }
                    />
                  </Routes>
                </Box>
              </ContentPane>
            </Container>
            <OrderWindowHOC />
            <GTTModalHOC />
            <PriceAlertModalHOC />
            <DashboardNotificationSnackbar exchanges={['MCX', 'NFO', 'CDS', 'NSE', 'ALL']} />
          </FundsProvider>
        </ContractProvider>
      </NotificationProvider>
    );
  }

  const appContent = (
    <>
      <CssBaseline />
      {mainContent}

      {/* Zoho Chatbot Integration - Conditional Rendering */}
      {brokerConfig.support?.type === 'zoho' && (
        <>
          {/* Login Zoho - Always on for login page */}
          {!isAuthenticated && (
            <ZohoChatbot
              key="login-zoho"
              ref={loginBotRef}
              isLoginPage={true}
              open={true}
              clientId={localStorage.getItem('login_id')}
              clientName={localStorage.getItem('login_name')}
              clientEmail={localStorage.getItem('login_email')}
            />
          )}

          {/* Support Zoho - loads on support page, auto-opens only after clean reload trigger */}
          {isAuthenticated && is2FAVerified && canLoadSupportBot && (
            <ZohoChatbot
              key="support-zoho"
              ref={zohoChatbotRef}
              isLoginPage={false}
              open={isSupportPage || showSupportChat}
              autoOpen={showSupportChat}
              clientId={userData?.client_id || localStorage.getItem('login_id')}
              clientName={userData?.name || localStorage.getItem('login_name')}
              clientEmail={userData?.email_id || localStorage.getItem('login_email')}
            />
          )}
        </>
      )}

      {/* CFD Risk Disclosure Modal */}
      <CFDRiskDisclosureModal
        open={showCFDModal}
        onClose={handleCFDModalClose}
        onAccept={handleCFDModalAccept}
      />
    </>
  );

  return brokerConfig.recaptchaSiteKey ? (
    <GoogleReCaptchaProvider reCaptchaKey={brokerConfig.recaptchaSiteKey}>
      {appContent}
    </GoogleReCaptchaProvider>
  ) : appContent;
};

export default App;
